import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useSearchParams } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useEffect, useState, type ReactNode } from "react";
import { createLabel, getLabelById, editLabel } from "../apiService";
import { LuChevronDown, LuLoaderCircle } from "react-icons/lu";
import { ChevronRight, Tag } from "lucide-react";
import { toast } from "sonner";
import { LuCircleCheck } from "react-icons/lu";
import ToastContent from "@/components/common/toast/ToastContent";
import { capitalizeFirstLetter } from "@/screens/utils/Helpers";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandGroup, CommandItem, CommandList } from "@/components/ui/command";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { getUniqueLabels } from "@/screens/creatives/apiService";
import { IconPhotoVideo } from "@tabler/icons-react";
import {
  RiColorFilterLine,
  RiCopperCoinLine,
  RiImage2Line,
  RiInformationLine,
  RiMovieLine,
} from "@remixicon/react";
import { cn } from "@/lib/utils";

interface LabelDescriptionDialogProps {
  onCreate: (labelId: string, labelTitle?: string) => Promise<void>;
  onEdit: (labelId: string, isTitleOnlyChange?: boolean) => Promise<void>;
  report_type?: string;
  /** Called when the create form is submitted (before API call), useful for showing loading states */
  onSubmitStart?: () => void;
  /** Called when the create API call fails, useful for resetting loading states */
  onSubmitError?: () => void;
}

type Option = {
  value: string | null;
  label: string;
  icon?: ReactNode;
  info?: string;
  disabled?: boolean;
  disabledInfo?: string;
};

const creativeTypes: Option[] = [
  {
    value: "image",
    label: "Image",
    icon: (
      <div className="h-[20px] w-[20px] flex justify-center items-center squircle-mask squircle-radius-[4px] bg-[#FF469924]">
        <RiImage2Line size={14} className="text-[#FF4699]" />
      </div>
    ),
  },
  {
    value: "video",
    label: "Video",
    icon: (
      <div className="h-[20px] w-[20px] flex justify-center items-center squircle-mask squircle-radius-[4px] bg-[#018C6224]">
        <RiMovieLine size={14} className="text-[#018C62]" />
      </div>
    ),
  },
  {
    value: null,
    label: "All",
    icon: (
      <div className="h-[20px] w-[20px] flex justify-center items-center squircle-mask squircle-radius-[4px] bg-[#5344FF24]">
        <IconPhotoVideo size={14} className="text-[#5344FF]" />
      </div>
    ),
  },
];

const groupTypes: Option[] = [
  {
    value: "unique",
    label: "Unique",
    icon: (
      <div className="h-[20px] w-[20px] flex justify-center items-center squircle-mask squircle-radius-[4px]">
        <RiCopperCoinLine size={14} className="text-brand-primary-400" />
      </div>
    ),
    info: "One creative, one group. Cleaner reports.",
  },
  {
    value: "multiple",
    label: "Multiple",
    icon: (
      <div className="h-[20px] w-[20px] flex justify-center items-center squircle-mask squircle-radius-[4px]">
        <RiColorFilterLine size={14} className="text-brand-primary-400" />
      </div>
    ),
    info: "Creatives can be in multiple groups.\n Great for cross-tagging & sequence.",
  },
];

export const LabelDescriptionDialog = ({
  onCreate,
  onEdit,
  report_type,
  onSubmitStart,
  onSubmitError,
}: LabelDescriptionDialogProps) => {
  const { accountId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [open, setOpen] = useState(false);

  const [selectedCreativeType, setSelectedCreativeType] = useState<Option>(
    creativeTypes[1],
  );

  const [selectedLabelType, setSelectedLabelType] = useState<Option>(groupTypes[0]);
  const type = searchParams.get("type");
  const isCreate = !searchParams.get("id");
  const isCustomPreset = searchParams.get("customPreset") === "true";
  const sectionId = searchParams.get("sectionId") ?? "";
  const [title, setTitle] = useState("");
  const [prompt, setPrompt] = useState("");
  const [initialPrompt, setInitialPrompt] = useState("");
  const [initialCreativeType, setInitialCreativeType] = useState<string | null>(null);
  const [initialLabelType, setInitialLabelType] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(isCreate ? false : true);
  const [id, setId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uniqueLabels, setUniqueLabels] = useState<string[]>([]);
  const [generateStatus, setGenerateStatus] = useState<string>();

  const POLLING_INTERVAL = 2000; // 2 seconds

  const getUniqueLabelsData = async (
    accountId: string,
    labelId: string,
    sectionId: string,
  ) => {
    try {
      const response = await getUniqueLabels(accountId, labelId, sectionId);
      setUniqueLabels(response.unique_labels || []);
      setGenerateStatus(response.status);
      return response.status;
    } catch (error) {
      console.error("Error fetching unique labels:", error);
      return "failed";
    }
  };

  useEffect(() => {
    if (!id || !accountId || !type) return;

    let timeoutId: NodeJS.Timeout;
    const pollData = async () => {
      const status = await getUniqueLabelsData(accountId, id, sectionId);
      if (status !== "success" && status !== "failed") {
        timeoutId = setTimeout(() => {
          void pollData();
        }, POLLING_INTERVAL);
      }
    };

    void pollData();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [id, accountId, type, sectionId]);

  useEffect(() => {
    setOpen(!!searchParams.get("type"));
    setId(searchParams.get("id") ?? "");
  }, [searchParams]);

  useEffect(() => {
    const fetchData = async () => {
      if (!id || !accountId || !type) return;
      setIsLoading(true);
      try {
        const labelData = await getLabelById(accountId, id);
        setTitle(labelData.title);
        setPrompt(labelData.prompt);
        setInitialPrompt(labelData.prompt);
        setInitialCreativeType(labelData.creative_type);
        setInitialLabelType(labelData.label_type);
        const creativeType = creativeTypes.find(
          (creativeType) => creativeType.value === labelData.creative_type,
        );
        if (creativeType) setSelectedCreativeType(creativeType);
        const groupType = groupTypes.find(
          (groupType) => groupType.value === labelData.label_type,
        );
        if (groupType) setSelectedLabelType(groupType);
      } catch (error) {
        console.error("Error fetching data:", error);
        // Handle error appropriately
      } finally {
        setIsLoading(false);
      }
    };

    void fetchData();
  }, [id, accountId, type]);

  // Ensure group type is valid when creative type changes
  useEffect(() => {
    const isAll = selectedCreativeType.value === null;
    if (isAll && selectedLabelType.value === "multiple") {
      setSelectedLabelType(groupTypes[0]);
    }
  }, [selectedCreativeType.value, selectedLabelType.value]);

  const subtitle = isCreate
    ? "Define a label to automatically group ads using info from their image or video."
    : `Refine the definition to improve how ads are grouped using image or video info.`;
  const buttonText = isCreate ? "Create" : "Update";

  const resetStates = () => {
    setTitle("");
    setPrompt("");
    setInitialPrompt("");
    setInitialCreativeType(null);
    setInitialLabelType(null);
    setGenerateStatus(undefined);
    setUniqueLabels([]);
  };

  const onClose = () => {
    setTimeout(() => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.delete("type");
        params.delete("id");
        params.delete("apply");
        params.delete("customPreset");
        return Object.fromEntries(params.entries());
      });
      resetStates();
    }, 100);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!accountId) {
      return;
    }

    if (isCreate) {
      // Optimistic: close the dialog immediately and run API calls in the background
      const labelData = {
        title,
        prompt,
        creative_type: selectedCreativeType.value ?? null,
        label_type: (selectedLabelType.value as "unique" | "multiple") ?? "unique",
        ...(report_type ? { report_type } : {}),
      };
      onSubmitStart?.();
      setOpen(false);
      onClose();

      void (async () => {
        try {
          const response = await createLabel(accountId, labelData);
          await onCreate(response.id, title);
        } catch (error) {
          console.error(`Error creating ${type}:`, error);
          onSubmitError?.();
        }
      })();
      return;
    }

    // Edit flow: keep the existing awaited behavior
    setIsSubmitting(true);
    try {
      const labelData = {
        title,
        prompt,
        creative_type: selectedCreativeType.value ?? null,
        label_type: (selectedLabelType.value as "unique" | "multiple") ?? "unique",
        ...(report_type ? { report_type } : {}),
      };

      await editLabel(accountId, id, labelData);

      // Check if anything other than title has changed
      const isTitleOnlyChange =
        prompt.trim() === initialPrompt.trim() && // prompt unchanged
        selectedCreativeType.value === initialCreativeType && // creative_type unchanged
        selectedLabelType.value === initialLabelType; // label_type unchanged
      await onEdit(id, isTitleOnlyChange);

      toast.message(
        <ToastContent
          icon={LuCircleCheck}
          headingPrefix={`${capitalizeFirstLetter(type ?? "")} updated`}
          subheadingSuffix={`${capitalizeFirstLetter(type ?? "")} has been updated successfully`}
          isError={false}
        />,
      );

      onClose();
    } catch (error) {
      console.error(`Error updating ${type}:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    return title.trim() !== "" && prompt.trim() !== "";
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-[716px] p-0  gap-6 !rounded-[12px]" hideCloseButton>
        <DialogHeader className="pt-[24px] pr-[32px] pl-[31px]">
          <DialogTitle className="overflow-hidden text-brand-primary-new text-ellipsis text-[15px] font-medium leading-[18px] tracking-[0.3px]">
            {isCustomPreset ? "Custom section" : `${isCreate ? "Create" : "Edit"} ${type}`}
          </DialogTitle>
          <p className="text-brand-primary-600 text-[14px] font-normal">{subtitle}</p>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center items-center h-full p-[50px]">
            <LuLoaderCircle className="h-4 w-4 animate-spin" />
          </div>
        ) : (
          <form
            onSubmit={(e) => {
              void handleSubmit(e);
            }}
          >
            <div className="pr-[32px] pl-[31px]">
              <Input
                placeholder={`Enter ${type} title`}
                className="w-full px-[14px] py-[6px] placeholder:text-[#A4ACC3] text-brand-primary-new rounded-[8px] border-[0.8px] border-[#D0D5DD99] bg-white shadow-none"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>
            <div className="pr-[32px] pl-[31px] mt-[10px]">
              <Textarea
                placeholder={`Write how you want your ads grouped, what to emphasize, and the data source - visuals, copy, or headlines. Don't worry if you're unsure; visuals are the default.`}
                className="min-h-[113px] px-[14px] py-[6px] resize-none placeholder:text-[#A4ACC3] focus-visible:ring-0 text-brand-primary-new/70 text-[14px] font-normal rounded-[8px] border-[0.8px] border-[#D0D5DD99] bg-white  shadow-none"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
              />
            </div>

            <div className="pr-[32px] pl-[31px] pb-[24px] pt-[20px] mt-[20px] flex items-center justify-between gap-4 border-t border-[#D0D5DD99]">
              <div className="flex gap-2">
                {/* Creative Type - Select Dropdown */}
                <MenuSelect
                  selected={selectedCreativeType}
                  options={creativeTypes}
                  onSelect={setSelectedCreativeType}
                />

                {/* Group Type - Select Dropdown */}
                <MenuSelect
                  selected={selectedLabelType}
                  options={groupTypes.map((opt) => ({
                    ...opt,
                    disabled:
                      opt.value === "multiple" && selectedCreativeType.value === null,
                    disabledInfo:
                      opt.value === "multiple" && selectedCreativeType.value === null
                        ? 'Multi-tagging is disabled in "All" because it mixes asset types.\n Switch to "Image" or "Video" to multi-tag. Sequences are Video-only.'
                        : undefined,
                  }))}
                  onSelect={setSelectedLabelType}
                />
                {/* Generated Labels Preview */}
                {generateStatus && generateStatus !== "failed" && (
                  <Popover>
                    <PopoverTrigger
                      asChild
                      disabled={generateStatus === "pending"}
                      className="disabled:opacity-100"
                    >
                      <Button
                        variant="outline"
                        className="gap-2 border-0 flex items-center bg-[#FCEDE5] hover:bg-[#FCEDE5]"
                      >
                        {generateStatus === "success" && (
                          <>
                            <Tag size={14} color="#FF4500" />
                            <span className="font-medium text-[12px] leading-[19.16px] tracking-[0.02em] text-[#FF4500]">
                              Groups
                            </span>
                            <ChevronRight size={14} color="#FF4500" />
                          </>
                        )}
                        {generateStatus === "pending" && (
                          <>
                            <LuLoaderCircle className="h-4 w-4 animate-spin text-[#FF4500] " />
                            <span className="font-medium text-[12px] leading-[19.16px] tracking-[0.02em] text-[#FF4500] relative top-[1px]">
                              Generating groups
                            </span>
                          </>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent
                      className="rounded-[4px] w-[200px] p-0"
                      side="right"
                      align="start"
                      portalled={false}
                    >
                      <Command className="max-h-[210px]">
                        <CommandList>
                          <CommandGroup>
                            {uniqueLabels.map((label, index) => (
                              <CommandItem
                                key={index}
                                className="focus:bg-accent focus:text-accent-foreground  min-h-8 h-auto"
                              >
                                <span className="text-brand-primary-new text-[12px] break-words">
                                  {label}
                                </span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={"outline"}
                  className="min-w-[69px] text-brand-primary-new font-medium text-[12px]"
                  onClick={() => {
                    setOpen(false);
                    onClose();
                  }}
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  className="min-w-[69px] bg-brand-orange hover:bg-hover-brand_orange font-medium text-white text-[12px] border-0"
                  disabled={!isFormValid() || isSubmitting}
                >
                  {isSubmitting ? (
                    <LuLoaderCircle className="h-4 w-4 animate-spin" />
                  ) : (
                    buttonText
                  )}
                </Button>
              </div>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Reusable MenuSelect component kept in the same file for local reuse
interface BaseOption {
  value: string | null;
  label: string;
  icon?: ReactNode;
  info?: string;
  disabled?: boolean;
  disabledInfo?: string;
}

interface MenuSelectProps<T extends BaseOption> {
  selected: T;
  options: T[];
  onSelect: (option: T) => void;
}

const MenuSelect = <T extends BaseOption>({
  selected,
  options,
  onSelect,
}: MenuSelectProps<T>) => {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger>
        <div className="h-[32px] border-border-gray border-[1px] flex items-center gap-[6px] px-[6px] pr-[12px] py-[6px] rounded-[8px] bg-white">
          {selected.icon ?? null}
          <span className="text-brand-primary-new mt-[2px] text-[12px] font-medium leading-[20px]">
            {selected.label}
          </span>
          <LuChevronDown className="h-4 w-4 text-brand-primary-new" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="rounded-[12px] p-1 border-[1px] border-border-gray z-[60]"
        align="start"
      >
        {options.map((opt, index) => {
          const item = (
            <DropdownMenuItem
              onSelect={(e) => {
                if (opt.disabled) {
                  e.preventDefault();
                  return;
                }
                onSelect(opt);
              }}
              key={index}
              disabled={opt.disabled}
              aria-disabled={opt.disabled}
              className={cn(
                "cursor-pointer flex h-[28px] px-[8px] py-[4px] justify-between items-center rounded-[8px] hover:bg-[#FBFBFB]",
                selected.value === opt.value && "bg-[#F6F6F6]",
              )}
            >
              <div className="flex items-center gap-[6px]">
                {opt.icon ?? null}
                <span className="text-brand-primary-800 mt-[2px] text-[12px] leading-[20px]">
                  {opt.label}
                </span>
              </div>
              {opt.info && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center">
                        <RiInformationLine size={14} className="text-brand-primary-400" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent
                      side="bottom"
                      align="start"
                      className="whitespace-pre-wrap"
                    >
                      {opt.info}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </DropdownMenuItem>
          );
          if (opt.disabled && opt.disabledInfo) {
            return (
              <TooltipProvider key={index}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="w-full">{item}</div>
                  </TooltipTrigger>
                  <TooltipContent
                    side="bottom"
                    align="start"
                    alignOffset={50}
                    className="text-[12px] whitespace-pre-wrap"
                  >
                    {opt.disabledInfo}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          }
          return item;
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
